import Badge from "@/components/base/Badge";
import { cn } from "@/utils/cn";
import { formatFcfa } from "@/utils/format";
import type { Compte, CompteType } from "../data/types";

type SectionKey = "proprietaires" | "immeubles" | "biens" | "locataires" | "contrats" | "employes";

interface SectionMeta {
  key: SectionKey;
  title: string;
  icon: string;
  tone: "primary" | "accent" | "secondary";
  emptyTitle: string;
  emptyText: string;
}

const SECTIONS: Record<SectionKey, SectionMeta> = {
  proprietaires: {
    key: "proprietaires",
    title: "Propriétaires rattachés",
    icon: "ri-user-star-line",
    tone: "primary",
    emptyTitle: "Aucun propriétaire rattaché",
    emptyText: "Les propriétaires gérés par cette agence apparaîtront ici une fois la base connectée.",
  },
  immeubles: {
    key: "immeubles",
    title: "Immeubles",
    icon: "ri-building-2-line",
    tone: "secondary",
    emptyTitle: "Aucun immeuble",
    emptyText: "Les immeubles de ce compte apparaîtront ici.",
  },
  biens: {
    key: "biens",
    title: "Biens",
    icon: "ri-home-4-line",
    tone: "accent",
    emptyTitle: "Aucun bien",
    emptyText: "Les appartements, boutiques et studios apparaîtront ici.",
  },
  locataires: {
    key: "locataires",
    title: "Locataires",
    icon: "ri-user-3-line",
    tone: "primary",
    emptyTitle: "Aucun locataire",
    emptyText: "Les locataires apparaîtront ici dès qu'un contrat les relie à un bien.",
  },
  contrats: {
    key: "contrats",
    title: "Contrats",
    icon: "ri-file-text-line",
    tone: "secondary",
    emptyTitle: "Aucun contrat",
    emptyText: "Les baux et échéances apparaîtront ici.",
  },
  employes: {
    key: "employes",
    title: "Employés",
    icon: "ri-team-line",
    tone: "accent",
    emptyTitle: "Aucun employé",
    emptyText: "Les membres de l'équipe de cette agence apparaîtront ici.",
  },
};

const TYPE_SECTIONS: Record<CompteType, SectionKey[]> = {
  agence: ["proprietaires", "immeubles", "biens", "locataires", "employes"],
  proprietaire: ["immeubles", "biens", "locataires", "contrats"],
  utilisateur: [],
};

const toneClasses: Record<SectionMeta["tone"], string> = {
  primary: "bg-primary-50 text-primary-600",
  accent: "bg-accent-50 text-accent-700",
  secondary: "bg-secondary-50 text-secondary-600",
};

function countFor(compte: Compte, key: SectionKey): number {
  return compte.rattachements[key].length;
}

interface CompteRelationsProps {
  compte: Compte;
}

export default function CompteRelations({ compte }: CompteRelationsProps) {
  const sections = TYPE_SECTIONS[compte.type];
  if (sections.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      {sections.map((key) => {
        const meta = SECTIONS[key];
        const count = countFor(compte, key);
        const items = compte.rattachements[key] as Array<Record<string, unknown>>;

        return (
          <section key={key} className="flex flex-col rounded-lg border border-background-200 bg-background-50">
            <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
              <span className="flex items-center gap-2.5">
                <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md", toneClasses[meta.tone])}>
                  <i className={cn(meta.icon, "text-base")} aria-hidden="true" />
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{meta.title}</h3>
              </span>
              <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
                {count}
              </span>
            </header>

            <div className="p-4 md:p-5">
              {count === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                  <span className={cn("flex h-12 w-12 items-center justify-center rounded-full bg-background-100", meta.tone === "primary" ? "text-primary-600" : meta.tone === "accent" ? "text-accent-700" : "text-secondary-600")}>
                    <i className={cn(meta.icon, "text-xl")} aria-hidden="true" />
                  </span>
                  <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">{meta.emptyTitle}</p>
                  <p className="mt-1 max-w-xs text-xs text-foreground-600">{meta.emptyText}</p>
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-background-200">
                  {items.map((item) => (
                    <li key={String(item.id)} className="py-3 first:pt-0 last:pb-0">
                      {key === "proprietaires" && (
                        <div className="min-w-0">
                          <p className="truncate text-sm font-label font-medium text-foreground-950">{String(item.nom)}</p>
                          <p className="mt-0.5 truncate text-xs text-foreground-500">
                            {String(item.telephone || "Téléphone non renseigné")}
                          </p>
                        </div>
                      )}
                      {key === "immeubles" && (
                        <div className="min-w-0">
                          <p className="truncate text-sm font-label font-medium text-foreground-950">{String(item.nom)}</p>
                          <p className="mt-0.5 truncate text-xs text-foreground-500">{String(item.ville || "Ville non renseignée")}</p>
                        </div>
                      )}
                      {key === "biens" && (
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-label font-medium text-foreground-950">{String(item.reference)}</p>
                            <p className="mt-0.5 truncate text-xs text-foreground-500">{String(item.immeubleNom)}</p>
                          </div>
                          <span className="shrink-0 text-xs font-label font-medium text-foreground-700">
                            {formatFcfa(Number(item.loyer))}
                          </span>
                        </div>
                      )}
                      {key === "locataires" && (
                        <div className="min-w-0">
                          <p className="truncate text-sm font-label font-medium text-foreground-950">{String(item.nom)}</p>
                          <p className="mt-0.5 truncate text-xs text-foreground-500">
                            {String(item.telephone || "Téléphone non renseigné")}
                          </p>
                        </div>
                      )}
                      {key === "contrats" && (
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-label font-medium text-foreground-950">
                              {String(item.reference)} · {String(item.bienReference)}
                            </p>
                            <p className="mt-0.5 truncate text-xs text-foreground-500">
                              {formatFcfa(Number(item.loyer))} / mois
                            </p>
                          </div>
                          {item.statut ? <Badge tone="neutral">{String(item.statut)}</Badge> : null}
                        </div>
                      )}
                      {key === "employes" && (
                        <div className="min-w-0">
                          <p className="truncate text-sm font-label font-medium text-foreground-950">{String(item.nom)}</p>
                          <p className="mt-0.5 truncate text-xs text-foreground-500">{String(item.role || "Rôle non défini")}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}